import { useEffect } from 'react';
import { onInstant } from '@/shared/api/websocket/realtime-sync.registry';
import {
  payloadObject,
  payloadString,
  payloadBool,
} from '@/shared/api/websocket/realtime-sync.payload';
import { useUser } from '@/app/model/stores';
import { isAngleValidationFinished } from '@/shared/constants/status';
import type { ValidationItem } from './types';
import { decodeValidation } from './decode-validation';

interface UseValidationListenerProps {
  setValidationItems: React.Dispatch<React.SetStateAction<ValidationItem[]>>;
  notSuitableImageMessage: string;
}

export function useValidationListener({
  setValidationItems,
  notSuitableImageMessage,
}: UseValidationListenerProps) {
  const user = useUser();

  useEffect(() => {
    if (!user) return;

    const cleanup = onInstant('angles', (event) => {
      if (event.action !== 'validation_completed') return;
      if (!event.id) return;

      const validation = payloadObject(event.payload, 'validation');
      if (!validation) return;

      const status = payloadString(validation, 'status');
      if (!status || !isAngleValidationFinished(status)) return;

      // One decoder for all three validation sources (initial / poll / WS) —
      // handles the lenient-vs-strict different-building split and every routed
      // outcome. See decode-validation.ts.
      const decoded = decodeValidation(
        {
          status,
          recommendations: payloadString(validation, 'recommendations'),
          routed_to_documents:
            payloadBool(validation, 'routed_to_documents') === true,
          routed_to_references:
            payloadBool(validation, 'routed_to_references') === true,
          routed_to_output_style:
            payloadBool(validation, 'routed_to_output_style') === true,
          validation_failure_reason: payloadString(
            validation,
            'validation_failure_reason'
          ),
          can_override_building:
            payloadBool(validation, 'can_override_building') === true,
          auto_accepted_as_view:
            payloadBool(validation, 'auto_accepted_as_view') === true,
        },
        notSuitableImageMessage
      );

      setValidationItems((prev) =>
        prev.map((item) =>
          item.id === event.id
            ? {
                ...item,
                status: decoded.status,
                note: decoded.note,
                error: decoded.error,
                validation: decoded.validation,
              }
            : item
        )
      );
    });

    return cleanup;
  }, [user, notSuitableImageMessage, setValidationItems]);
}
